import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { bodyFont, headingFontBold, labelFontSemiBold, vars } from '../../../../../Theme';
import { INewAssemblyIndexErrorState, INewAssemblyIndexMainState } from '../NewAssemblyIndexDialog';

import { mdiCubeOutline, mdiAlertCircle } from '@mdi/js';
import AppIcon from '../../../Icons/AppIcon';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import CheckboxTree from '../../_parts/CheckboxTree';
import Message from '../../_parts/Message';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const partialModelLabelSx: SxProps = {
    display: 'flex',
    minHeight: '40px',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    fontFamily: headingFontBold,
    gap: '8px',
    color: vars.black,

    '&.Mui-focused': { color: vars.black },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

export default ({ mainState, handleChange, showErrorState }: INewAssemblyIndexProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'newAssemblyIndexDialog' });
    const { data: newIndexPartialModels } = useSelector(
        (_store: IAppReduxStore) => _store.projectNewAssemblyIndex,
    );
    const docTypesConfig = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);

    useEffect(() => {
        if (newIndexPartialModels.partialModels?.length > 0) {
            handleChange({
                field: 'selectedPartialModelIds',
                value: newIndexPartialModels.partialModels
                    .filter(partialModel => partialModel.isLinked || partialModel.newIndex)
                    .map(partialModel => partialModel.universalId),
            });
        }
    }, [newIndexPartialModels]);

    const items = useMemo(() => {
        if (newIndexPartialModels.partialModels?.length > 0) {
            return newIndexPartialModels.partialModels.map(partialModel => {
                const partialModelDocTypeConfig = docTypesConfig.find(
                    docTypeConfig => docTypeConfig.name === partialModel.docType,
                );

                return {
                    id: partialModel.universalId,
                    label: partialModel.subject,
                    parent: '',
                    path: partialModel.subject,
                    icon: {
                        childIcon: {
                            path: partialModelDocTypeConfig?.icon,
                            color: partialModelDocTypeConfig?.color,
                        },
                    },
                    chipLabel:
                        partialModel.newIndex && `${t('newIndex')}: ${partialModel.newIndex}`,
                };
            });
        }
    }, [newIndexPartialModels]);

    const handleNodeCheck = (id: string, isChecked: boolean) => {
        handleChange({
            field: 'selectedPartialModelIds',
            value: isChecked
                ? [...mainState.selectedPartialModelIds, id]
                : mainState.selectedPartialModelIds.filter(item => item !== id),
        });
    };

    const handleSelectDeselectAll = (_, checked: boolean) => {
        handleChange({
            field: 'selectedPartialModelIds',
            value: checked ? items.map(item => item.id) : [],
        });
    };

    return (
        <Stack spacing={'16px'}>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('nextIndex')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    disabled
                    value={mainState?.nextIndex || t('indexNotFound')}
                    error={showErrorState.nextIndexError}
                />
                {showErrorState.nextIndexError && (
                    <Message
                        text={t('noNextIndexError')}
                        icon={mdiAlertCircle}
                        color={vars.errorDialogRed}
                        typographySx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            alignSelf: 'center',
                        }}
                        wrapperSx={{
                            border: 'none',
                            padding: '2px',
                            margin: '0px',
                            gap: '4px',
                        }}
                        iconWrapperSx={{ display: 'flex' }}
                        iconSize={16 / 24}
                    />
                )}
            </FormControl>

            <FormControl>
                <FormLabel sx={formLabelSx}>{t('subject')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({
                            field: 'subject',
                            value: e.target.value,
                        })
                    }
                    placeholder={t('enterSubject')}
                    value={mainState.subject}
                />
            </FormControl>

            <FormControl>
                <FormLabel sx={partialModelLabelSx}>
                    <AppIcon path={mdiCubeOutline} color={vars.font} size={1} />
                    {t('partialModels')}
                </FormLabel>
                <Stack sx={{ p: 0 }}>
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <FormLabel required sx={{ ...formLabelSx }}>
                            {t('choosePartialModels')}
                        </FormLabel>
                        <FormControlLabel
                            disabled={items?.length === 0}
                            label={
                                items?.length !== mainState?.selectedPartialModelIds?.length
                                    ? t('selectAll')
                                    : t('clear')
                            }
                            labelPlacement='start'
                            sx={{
                                marginBottom: '8px',
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: labelFontSemiBold,
                                    fontSize: '14px',
                                },
                            }}
                            control={
                                <Checkbox
                                    disabled={items?.length === 0}
                                    sx={{ padding: 0, paddingX: '9px' }}
                                    onChange={handleSelectDeselectAll}
                                    checked={
                                        items?.length === mainState?.selectedPartialModelIds?.length
                                    }
                                />
                            }
                        />
                    </Stack>

                    {showErrorState.selectedPartialModelsError && (
                        <Message
                            text={t('selectedPartialModelsError')}
                            icon={mdiAlertCircle}
                            color={vars.errorDialogRed}
                            typographySx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                alignSelf: 'center',
                            }}
                            wrapperSx={{
                                border: 'none',
                                padding: '2px',
                                marginTop: '0px',
                                gap: '4px',
                            }}
                            iconWrapperSx={{ display: 'flex' }}
                            iconSize={16 / 24}
                        />
                    )}

                    <CheckboxTree
                        hasError={showErrorState.selectedPartialModelsError}
                        items={items ?? []}
                        checkedNodes={mainState?.selectedPartialModelIds}
                        onCheckNode={handleNodeCheck}
                        simpleTreeViewSx={{ overflow: 'auto', maxHeight: '360px' }}
                    />
                </Stack>
            </FormControl>
        </Stack>
    );
};

export interface INewAssemblyIndexProps {
    mainState: INewAssemblyIndexMainState;
    handleChange: ({ field, value }: { field: string; value: string | string[] | number }) => void;
    showErrorState: INewAssemblyIndexErrorState;
}
